import { Outlet, BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, type ToasterProps } from 'react-hot-toast';

// react-hot-toast v2 FC return type is incompatible with @types/react 18.3+
const HotToaster = Toaster as React.ComponentType<ToasterProps>;
import { AuthProvider } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { OrgProvider } from './context/OrgContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import OrgChooserPage from './pages/OrgChooserPage';
import CreateOrgPage from './pages/CreateOrgPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import DashboardPage from './pages/DashboardPage';
import CustomerListPage from './pages/CustomerListPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AddCustomerPage from './pages/AddCustomerPage';
import EditCustomerPage from './pages/EditCustomerPage';
import CropTypesPage from './pages/CropTypesPage';
import RegionsPage from './pages/RegionsPage';
import SeasonTimesPage from './pages/SeasonTimesPage';
import CustomerLogsPage from './pages/CustomerLogs/CustomerLogsPage';
import CropStatusPage from './pages/CropStatusPage';
import RolesPage from './pages/RolesPage';
import MembersPage from './pages/MembersPage';
import OrgSettingsPage from './pages/OrgSettingsPage';
import UserProfilePage from './pages/UserProfilePage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminOrgView from './pages/SuperAdminOrgView';

/**
 * Layout for org-scoped routes (/org/:orgSlug/*).
 * OrgProvider resolves the org from :orgSlug and provides OrgContext.
 * The inner PermissionsProvider reads OrgContext and loads org-scoped permissions.
 * The outer (global) PermissionsProvider in App still provides appUser to OrgProvider.
 */
function OrgLayout() {
  return (
    <OrgProvider>
      <PermissionsProvider>
        <Outlet />
      </PermissionsProvider>
    </OrgProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          {/*
           * Global PermissionsProvider: loads appUser (isSuperAdmin, isDisabled).
           * orgId=null here → permissions=[]. OrgProvider uses this for the appUser check.
           * Inside /org/:orgSlug routes an inner PermissionsProvider shadows this one
           * and loads org-scoped permissions with the real orgId from OrgContext.
           */}
          <PermissionsProvider>
            <HotToaster position="top-right" />
            <Routes>
              {/* Public — redirect to / if already signed in */}
              <Route
                path="/login"
                element={
                  <PublicOnlyRoute>
                    <LoginPage />
                  </PublicOnlyRoute>
                }
              />

              {/* Global home — org chooser */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <OrgChooserPage />
                  </ProtectedRoute>
                }
              />

              {/* Create org — must be before /org/:orgSlug to avoid param match */}
              <Route
                path="/org/new"
                element={
                  <ProtectedRoute>
                    <CreateOrgPage />
                  </ProtectedRoute>
                }
              />

              {/* User profile — global user profile page */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Invite accept — handles auth internally, not mounted under OrgProvider */}
              <Route
                path="/invite/:token"
                element={<InviteAcceptPage />}
              />

              {/* Super admin dashboard */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Super admin org view — /admin/orgs/:slug */}
              <Route
                path="/admin/orgs/:orgSlug"
                element={
                  <ProtectedRoute>
                    <OrgProvider>
                      <PermissionsProvider>
                        <SuperAdminOrgView />
                      </PermissionsProvider>
                    </OrgProvider>
                  </ProtectedRoute>
                }
              />

              {/* Org-scoped routes — all under /org/:orgSlug */}
              <Route
                path="/org/:orgSlug"
                element={
                  <ProtectedRoute>
                    <OrgLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route
                  path="customers"
                  element={
                    <ProtectedRoute permission="customers.read">
                      <CustomerListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="customers/new"
                  element={
                    <ProtectedRoute permission="customers.write">
                      <AddCustomerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="customers/:id"
                  element={
                    <ProtectedRoute permission="customers.read">
                      <CustomerDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="customers/:id/edit"
                  element={
                    <ProtectedRoute permission="customers.write">
                      <EditCustomerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="customers/:id/logs"
                  element={
                    <ProtectedRoute permission="customers.read">
                      <CustomerLogsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="crop-types"
                  element={
                    <ProtectedRoute permission="cropTypes.manage">
                      <CropTypesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="regions"
                  element={
                    <ProtectedRoute permission="regions.manage">
                      <RegionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="season-times"
                  element={
                    <ProtectedRoute permission="seasonTimes.manage">
                      <SeasonTimesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="crop-status"
                  element={
                    <ProtectedRoute permission="cropStatus.read">
                      <CropStatusPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute permission="users.manage">
                      <MembersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="roles"
                  element={
                    <ProtectedRoute permission="users.manage">
                      <RolesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute permission="users.manage">
                      <OrgSettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
